/**
 * Centralized responsive breakpoint hook.
 *
 * Every responsive rule in the app goes through this. Direct
 * `useWindowDimensions()` reads in screens are banned (enforced by grep).
 *
 * Returns a combined API: legacy booleans (for back-compat with screens
 * that still use `isDesktop` / `isWide`) + a modern `bp` key and an
 * `atLeast()` predicate for cleaner breakpoint comparisons.
 *
 * Important: `isDesktop` is a semantic app-shell mode, not merely
 * `width >= 1024`. Android/iPad tablets can cross that width in
 * landscape, but they still need touch-first navigation and stacked
 * content. Desktop mode is therefore large web + non-mobile/tablet client,
 * which keeps Windows/macOS touch-screen PCs in the desktop shell.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * UNA SOLA RESPUESTA PARA TODA LA APP, Y POR QUE HIZO FALTA.
 *
 * Este hook tenia estado POR INSTANCIA: cada uno de sus 36 llamantes corria su
 * propio `useState` y su propio efecto. Nada garantizaba que dos componentes
 * contestaran lo mismo, y no lo contestaban.
 *
 * Medido con Playwright: al volver atras desde la ficha de un carro, el DOM
 * quedaba con la barra de navegacion de ESCRITORIO y la barra de pestanas MOVIL
 * al mismo tiempo, y la pantalla de explorar en blanco -- 491 nodos antes, 146
 * despues, sin un solo error de consola. `AppShell` decia escritorio y
 * `(tabs)/_layout` decia movil, con el mismo hook, en el mismo instante.
 *
 * La causa es que `useWindowDimensions()` puede devolver 0 durante un remontaje,
 * y el consumidor que monta en ese instante se queda PEGADO: su efecto solo se
 * vuelve a disparar si cambia una media query, y volver atras no cambia ninguna.
 * Un valor que se puede latchear mal y nunca corregirse, replicado 36 veces.
 *
 * Se arregla con las dos mitades del problema, no con una:
 *   1. `BreakpointProvider` calcula el estado UNA vez, arriba de todo, y todos
 *      leen ese. Que dos partes de la pantalla discrepen deja de ser posible.
 *   2. El ancho 0 deja de tomarse como respuesta: se cae a la medida real de la
 *      ventana. Sin esto, un provider unico solo garantizaria que todos se
 *      equivoquen a la vez.
 *
 * `useBreakpoint()` sigue funcionando sin provider --calcula local-- para que
 * una prueba o un render aislado no exploten. Pero en la app hay provider, y
 * `validate-single-breakpoint.mjs` comprueba que siga montado.
 * ─────────────────────────────────────────────────────────────────────────────
 */
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { Dimensions, Platform, useWindowDimensions } from 'react-native'
import { BREAKPOINTS, type BreakpointKey } from '../theme/breakpoints'

export interface BreakpointState {
  isMobile: boolean
  isTablet: boolean
  isDesktop: boolean
  isWide: boolean
  width: number
  /** Current breakpoint key: 'sm' | 'md' | 'lg' | 'xl'. */
  bp: BreakpointKey
  /** `atLeast('lg')` → true when width ≥ 1024. Preferred over ad-hoc comparisons. */
  atLeast: (key: BreakpointKey) => boolean
}

function resolveBp(width: number): BreakpointKey {
  if (width >= BREAKPOINTS.xl) return 'xl'
  if (width >= BREAKPOINTS.lg) return 'lg'
  if (width >= BREAKPOINTS.md) return 'md'
  return 'sm'
}

function resolveMobileLikeWebClient(): boolean {
  if (Platform.OS !== 'web') return false
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false

  const nav = window.navigator as Navigator & {
    userAgentData?: { mobile?: boolean; platform?: string }
    maxTouchPoints?: number
  }
  const ua = nav.userAgent ?? ''
  const platform = nav.userAgentData?.platform ?? nav.platform ?? ''
  const normalized = `${ua} ${platform}`.toLowerCase()

  // Android/iOS tablets can be wider than 1024px in landscape, but they
  // should keep touch-first navigation. Desktop Windows/macOS touch screens
  // often report a coarse primary pointer, so pointer media alone is not
  // enough to decide app-shell mode.
  if (nav.userAgentData?.mobile) return true
  if (/android|iphone|ipad|ipod|tablet/.test(normalized)) return true
  if (/mobile/.test(normalized) && !/windows/.test(normalized)) return true

  // iPadOS desktop-mode Safari reports "Macintosh" with touch points.
  if (/macintosh/.test(normalized) && (nav.maxTouchPoints ?? 0) > 1) return true

  return false
}

function resolveDesktopWebClient(): boolean {
  if (Platform.OS !== 'web') return false
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false
  if (resolveMobileLikeWebClient()) return false

  return true
}

/**
 * El ancho de la ventana, y nunca 0.
 *
 * `useWindowDimensions()` puede devolver 0 durante un remontaje en react-native-
 * web. Cero no es un ancho: es "todavia no se". Tomarlo por bueno mete a la app
 * en modo movil y ahi se queda, porque nada vuelve a preguntar.
 */
function useStableWidth(): number {
  const { width } = useWindowDimensions()
  if (width > 0) return width
  const fallback = Dimensions.get('window').width
  if (fallback > 0) return fallback
  if (Platform.OS === 'web' && typeof window !== 'undefined' && window.innerWidth > 0) {
    return window.innerWidth
  }
  // Ultimo recurso: el breakpoint de escritorio. Si de verdad no se sabe el
  // ancho, es mejor equivocarse hacia la pantalla que muestra mas -- una lista
  // angosta en un monitor es fea; un armazon de escritorio en un telefono es
  // inusable, pero eso ya lo descarta `isDesktopWebClient`.
  return BREAKPOINTS.lg
}

function useBreakpointValue(): BreakpointState {
  const width = useStableWidth()
  const [isDesktopWebClient, setIsDesktopWebClient] = useState(resolveDesktopWebClient)
  const bp = resolveBp(width)
  const isDesktop = width >= BREAKPOINTS.lg && isDesktopWebClient

  useEffect(() => {
    if (Platform.OS !== 'web') return
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return

    const hoverQuery = window.matchMedia('(hover: hover) and (pointer: fine)')
    const coarseQuery = window.matchMedia('(pointer: coarse)')
    const onChange = () => setIsDesktopWebClient(resolveDesktopWebClient())
    onChange()

    if (typeof hoverQuery.addEventListener === 'function') {
      hoverQuery.addEventListener('change', onChange)
      coarseQuery.addEventListener('change', onChange)
      return () => {
        hoverQuery.removeEventListener('change', onChange)
        coarseQuery.removeEventListener('change', onChange)
      }
    }

    hoverQuery.addListener(onChange)
    coarseQuery.addListener(onChange)
    return () => {
      hoverQuery.removeListener(onChange)
      coarseQuery.removeListener(onChange)
    }
  }, [])

  return useMemo(
    () => ({
      isMobile: width < BREAKPOINTS.md,
      isTablet: width >= BREAKPOINTS.md && !isDesktop,
      isDesktop,
      isWide: width >= BREAKPOINTS.xl,
      width,
      bp,
      atLeast: (key: BreakpointKey) => width >= BREAKPOINTS[key],
    }),
    [width, isDesktop, bp],
  )
}

const BreakpointContext = createContext<BreakpointState | null>(null)

/**
 * Se monta UNA vez, lo mas arriba posible. Todo lo que haya debajo comparte la
 * misma respuesta, asi que dos partes de la pantalla no pueden discrepar.
 */
export function BreakpointProvider({ children }: { children: ReactNode }) {
  const value = useBreakpointValue()
  return <BreakpointContext.Provider value={value}>{children}</BreakpointContext.Provider>
}

export function useBreakpoint(): BreakpointState {
  // El hook local se llama SIEMPRE porque las reglas de hooks no admiten
  // llamadas condicionales. Su valor solo se usa cuando no hay provider, que es
  // el caso de una prueba unitaria o un render aislado.
  const local = useBreakpointValue()
  const shared = useContext(BreakpointContext)
  return shared ?? local
}
